import { createElement } from 'react';
import { createTheme, useMediaQuery } from '@mui/material';
import type {
    PaletteMode,
    PaletteOptions,
    Theme,
    ThemeOptions,
    Breakpoint,
} from '@mui/material';
import type { Localization } from '@mui/material/locale';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import hexRgb from 'hex-rgb';
import binoTheme from './theme';
import type { BinoTheme, NamedColors } from './theme';
// Importing from { ArrowDownIcon } from components or components/icon gave a "Maximum call stack size exceeded" error
import ArrowDownIcon from './components/icons/assets/arrow-down.svg?react';
import { darkModeBackgroundImageElevation } from './utils/darkModeBackgroundImageElevation';

export type { NamedColors };

/* For use in material-ui/src/styles/colors.js */

/* http://mcg.mbitson.com/ */
export const primary = {
    _light: '#a869f0', // From material.io/color
    light: '#9062ca', // From Binosight STYLEGUIDE
    main: '#753bbd',
    _dark: '#42088c', // From material.io/color
    dark: '#5d2f97', // From Binosight STYLEGUIDE

    '50': '#eee7f7',
    '100': '#d6c4eb',
    '200': '#ba9dde',
    '300': '#9e76d1',
    '400': '#8a58c7',
    '500': '#753bbd',
    '600': '#6d35b7',
    '700': '#622dae',
    '800': '#5826a6',
    '900': '#451998',
    A100: '#decfff',
    A200: '#bb9cff',
    A400: '#9869ff',
    A700: '#864fff',
    contrastDefaultColor: 'light',
};

//
export const palepurple = {
    light: '#c5ace3',
    main: '#ac89d7', // 300
    dark: '#9b76cd', // 700

    '50': '#f5f1fa',
    '100': '#e6dcf3',
    '200': '#d6c4eb',
    '300': '#c5ace3',
    '400': '#b89bdd',
    '500': '#ac89d7',
    '600': '#a581d3',
    '700': '#9b76cd',
    '800': '#926cc7',
    '900': '#8259be',
    A100: '#ffffff',
    A200: '#f7f1ff',
    A400: '#d8beff',
    A700: '#c9a5ff',
    contrastDefaultColor: 'dark',
};

export const lightpurple = {
    light: '#b191da',
    main: '#9062ca', // 300
    dark: '#7d50bd', // 700

    '50': '#f2ecf9',
    '100': '#ded0ef',
    '200': '#c8b1e5',
    '300': '#b191da',
    '400': '#a17ad2',
    '500': '#9062ca',
    '600': '#885ac5',
    '700': '#7d50bd',
    '800': '#7346b7',
    '900': '#6134ab',
    A100: '#f8f4ff',
    A200: '#d7c1ff',
    A400: '#b68eff',
    A700: '#a674ff',
    contrastDefaultColor: 'light',
};

export const darkpurple = {
    main: '#5d2f97', // 300
    light: '#8e6db6',
    dark: '#4b2384', // 700

    '50': '#ece6f3',
    '100': '#cec1e0',
    '200': '#ae97cb',
    '300': '#8e6db6',
    '400': '#754ea7',
    '500': '#5d2f97',
    '600': '#552a8f',
    '700': '#4b2384',
    '800': '#411d7a',
    '900': '#301269',
    A100: '#bd9fff',
    A200: '#996cff',
    A400: '#7639ff',
    A700: '#641fff',
    contrastDefaultColor: 'light',
};

const getMuiThemeConfig = (mode: PaletteMode): ThemeOptions => {
    const primaryColor = mode === 'dark' ? palepurple : primary;
    const secondaryColor = mode === 'dark' ? primary : lightpurple;
    const primaryTextColor =
        mode === 'light' ? binoTheme.color.charcoal : binoTheme.color.snow;
    const secondaryTextColor =
        mode === 'light' ? binoTheme.color.darkGray : binoTheme.color.gray;
    const palette: PaletteOptions = {
        mode: mode,
        primary: primaryColor,
        secondary: secondaryColor,
        text: {
            primary: primaryTextColor,
            secondary: secondaryTextColor,
        },
        background: {
            paper:
                mode === 'light'
                    ? binoTheme.color.white
                    : binoTheme.color.black,
            default:
                mode === 'light' ? binoTheme.color.backGroundGray : '#121212',
        },
        white: {
            main:
                mode === 'light'
                    ? binoTheme.color.white
                    : binoTheme.color.black,
        },
        gray: {
            main: binoTheme.color.gray,
        },
        // purple: primary,
        // palepurple,
        // lightpurple,
        // darkpurple
    };
    const components: ThemeOptions['components'] = {
        MuiAutocomplete: {
            styleOverrides: {
                popupIndicator: ({ theme }) => ({
                    transition: 'transform 0.25s',
                    width: theme.spacing(3.5),
                    height: theme.spacing(3.5),
                }),
                popupIndicatorOpen: {
                    transform: 'scaleY(-1)',
                },
            },
            defaultProps: {
                popupIcon: createElement(ArrowDownIcon),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {},
                containedPrimary: {
                    '&.Mui-disabled': {
                        // The default is a black with transparency. It looks better
                        // color: theme.color.white,
                        // borderColor: 'transparent',
                        backgroundColor:
                            binoTheme.color.backgroundLightPurpleRGBA,
                    },
                },
            },
            variants: [
                {
                    props: { size: 'medium' },
                    style: {
                        height: 42,
                        fontSize: 12,
                        lineHeight: '16px',
                        textTransform: 'uppercase',
                    },
                },
                {
                    props: { size: 'small' },
                    style: {
                        fontSize: 12,
                    },
                },
                {
                    props: {
                        color: 'primary',
                        variant: 'containedInverted',
                    },
                    style: ({ theme }) => ({
                        background: theme.palette.background.paper,
                        color: binoTheme.color.primary,
                        border: `solid 1px ${binoTheme.color.primary}`,
                        '&:hover': {
                            boxShadow: theme.shadows[4],
                        },
                        ...(mode === 'dark' && {
                            color: binoTheme.color.lightPurple,
                            borderColor: binoTheme.color.lightPurple,
                        }),
                    }),
                },
                {
                    props: {
                        color: 'secondary',
                        variant: 'text',
                    },
                    style: ({ theme }) => ({
                        ...(mode === 'dark' && {
                            color: binoTheme.color.lightPurple,
                        }),
                    }),
                },
                {
                    props: { color: 'gray', variant: 'text' },
                    style: {
                        '&:hover': {
                            color: primaryColor,
                            backgroundColor:
                                mode === 'dark'
                                    ? 'rgba(128, 128, 128, 0.1)'
                                    : binoTheme.color.backgroundLightPurpleRGBA,
                        },
                    },
                },
            ],
        },
        MuiCssBaseline: {
            styleOverrides: (theme) => ({
                body: {
                    fontFamily: 'AvertaStandard, sans-serif',
                    //                    background: mode === 'light' ? binoTheme.color.backGroundGray : '#444444',
                    '& svg': {
                        // Perhaps we can remove this later, but all our SVG's seems
                        // to have issues with border-box + inherit
                        boxSizing: 'content-box',
                    },
                    '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                },
            }),
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekContainer: {
                    margin: 0,
                },
                weekNumberLabel: {
                    width: 48,
                },
                weekNumber: ({ theme }) => ({
                    width: 48,
                    fontWeight: 'bold',
                    background:
                        theme.palette.mode === 'dark'
                            ? undefined
                            : theme.palette.background.default,
                    backgroundImage: darkModeBackgroundImageElevation(
                        theme,
                        10,
                    ),
                    [theme.breakpoints.up('xs')]: {
                        fontSize: 12,
                    },
                }),
            },
        },
        MuiPickersLayout: {
            styleOverrides: {},
        },
        MuiPickersMonth: {
            styleOverrides: {
                root: {},
            },
        },
        MuiPickersDay: {
            styleOverrides: {
                // Not respected by MuiPickersDay for some reason
                // disabled: {
                // ...
                // },
                root: ({ ownerState, theme }) => ({
                    ...(ownerState.disabled
                        ? {
                              '&:before': {
                                  content: '""',
                                  //borderTop: `solid 1px rgba(0,0,0,0.1)`,
                                  backgroundColor:
                                      theme.palette.mode === 'dark'
                                          ? 'rgba(255,255,255,0.15)'
                                          : 'rgba(0,0,0,0.1)',
                                  transform: 'rotate(-30deg)',
                                  position: 'absolute',
                                  width: 24,
                                  height: 1,
                              },
                          }
                        : undefined),
                }),

                //     isWeekend(ownerState.day) ? {
                //     //textDecoration: 'underline'
                //     // position: 'relative',
                //     // '&:before': {
                //     //     content: '""',
                //     //     position: 'absolute',
                //     //     width: 'calc(100% + 4px)',
                //     //     marginLeft: -2,
                //     //     height: '100%',
                //     //     background: 'rgba(0,0,0,0.05)',

                //     // },

                // } : ownerState.disabled ? {
                //     '&:before': {
                //         content: '""',
                //         borderTop: 'solid 1px #ccc',
                //         transform: 'rotate(45deg)',
                //         position: 'absolute',
                //     },
                // }: null
            },
        },
        MuiDateCalendar: {
            defaultProps: {
                displayWeekNumber: true,
                showDaysOutsideCurrentMonth: true,
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    maxWidth: '100%',
                    width: 700,
                    padding: 12,
                    boxSizing: 'border-box',
                    scrollBehaviour: 'smooth',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: ({ ownerState }) => {
                    return {
                        fontWeight: ownerState.checked ? 'bold' : undefined,
                    };
                },
            },
        },
        // MuiTab: {
        //     styleOverrides: {
        //         root: {
        //             '&.Mui-selected': {
        //                 backgroundColor: binoTheme.color.primary,
        //                 color: binoTheme.color.white,
        //             },
        //         },
        //     },
        // },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                inputProps: {
                    autoComplete: 'off',
                    // Prevent lastpass
                    'data-lpignore': 'true',
                },
            },
        },
        MuiFormControl: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        // MuiInput: {
        //     styleOverrides: {
        //         input: {
        //             paddingTop: 12,
        //             paddingBottom: 12,
        //         },
        //     },
        // },
        // MuiInputLabel: {
        //     styleOverrides: {
        //         root: ({theme}) => ({
        //             color: theme.palette.text.primary,
        //             // '&[data-shrink="false"]': {
        //             //     transform: 'translate(14px, 14px) scale(1)',
        //             // },
        //         }),
        //         shrink: ({theme}) => ({
        //             color: theme.palette.text.secondary,
        //         }),
        //     },
        // },
        // MuiOutlinedInput: {
        //     styleOverrides: {
        //         input: {
        //             paddingTop: 12,
        //             paddingBottom: 12,
        //         },
        //     },
        // },
        MuiSelect: {
            defaultProps: {
                IconComponent: ArrowDownIcon,
            },
            styleOverrides: {
                icon: {
                    height: '1em',
                    right: '11px',
                    transition: 'transform 150ms ease',
                },
                iconOpen: {
                    transform: 'scaleY(-1)',
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    fontSize: 16,
                    padding: `8px 16px`,
                    color: theme.palette.getContrastText(
                        binoTheme.color.darkerPurple,
                    ),
                    backgroundColor: hexRgb(binoTheme.color.darkerPurple, {
                        format: 'css',
                        alpha: 0.95,
                    }),
                    boxShadow: '3px 3px 8px rgba(0,0,0,0.5)',
                }),
                arrow: {
                    color: hexRgb(binoTheme.color.darkerPurple, {
                        format: 'css',
                        alpha: 0.95,
                    }),
                    transform: 'scale(2)',
                },
            },
        },
        MuiTypography: {
            variants: [
                {
                    props: {
                        variant: 'label',
                    },
                    style: ({ theme }) => ({
                        letterSpacing: '0.6px',
                        fontWeight: 'bold',
                        fontSize: 12,
                        lineHeight: '16px',
                        color: theme.palette.text.secondary,
                    }),
                },
                {
                    props: {
                        variant: 'body1',
                    },
                    style: ({ theme }) => ({
                        fontSize: '16px',
                        lineHeight: '24px',
                        fontWeight: 'normal',
                        [theme.breakpoints.up('sm')]: {
                            fontSize: '18px',
                            lineHeight: '28px',
                        },
                    }),
                },
            ],
            styleOverrides: {
                h1: ({ theme }) => ({
                    fontSize: '24px',
                    lineHeight: '24px',
                    fontWeight: 'bold',
                    //margin: '0 0 16px 0',
                    //color: theme.palette.text.primary,
                    //padding: 0,
                    [theme.breakpoints.up('sm')]: {
                        fontSize: '44px',
                        lineHeight: '56px',
                        //  margin: '0 0 24px 0',
                    },
                }),
                h2: ({ theme }) => ({
                    fontSize: '20px',
                    lineHeight: '28px',
                    //margin: '0 0 16px 0',
                    fontWeight: 'bold',
                    //color: theme.palette.text.primary,
                    //padding: 0,
                    [theme.breakpoints.up('sm')]: {
                        fontSize: '32px',
                        lineHeight: '36px',
                        //margin: '0 0 24px 0',
                    },
                }),
                h3: ({ theme }) => ({
                    fontSize: '16px',
                    fontWeight: 'bold',
                    lineHeight: '24px',
                    //margin: '0 0 16px 0',
                    //color: theme.palette.text.primary,
                    //padding: 0,
                    [theme.breakpoints.up('sm')]: {
                        fontSize: '24px',
                        lineHeight: '34px',
                        //margin: '0 0 24px 0',
                    },
                }),
                h4: ({ theme }) => ({
                    fontSize: '14px',
                    fontWeight: 'bold',
                    lineHeight: '22px',
                    //margin: '0 0 16px 0',
                    //color: theme.palette.text.primary,
                    //padding: 0,
                    [theme.breakpoints.up('sm')]: {
                        fontSize: '20px',
                        lineHeight: '28px',
                        //margin: '0 0 24px 0',
                    },
                }),
                h5: ({ theme }) => ({
                    fontSize: '12px',
                    fontWeight: 'bold',
                    lineHeight: '16px',
                    //margin: margin === 'none' ? null : '0 0 16px 0',
                    //color: theme.palette.text.primary,
                    //padding: 0,
                    [theme.breakpoints.up('sm')]: {
                        // [`&.${typographyClasses.gutterBottom}`]: {
                        //     mb: 2,
                        // }
                    },
                }),
                body2: () => ({
                    fontSize: '14px',
                    lineHeight: '22px',
                    fontWeight: 'normal',
                }),
            },
        },
    };
    return {
        // binoTheme is basically just colors, we should incorporate this into palette
        bino: binoTheme, // todo darkmode for binoTheme. It will be tricky to do due to not taking dark-mode into consideration
        palette,
        components,
        typography: {
            fontFamily: '"AvertaStandard", sans-serif',
            fontSize: 14,
            fontWeightLight: 'normal',
            fontWeightRegular: 'normal',
            fontWeightMedium: 'bold',
            fontWeightBold: 'bold',
            h6: undefined, // We only have h1 to h5
            // button: {
            //     fontSize: 12,
            //     lineHeight: '16px',x
            //     textTransform: 'uppercase',
            // },
        },
    };
};

export const createMuiTheme = (
    locale: Localization,
    mode: PaletteMode = 'light',
): Theme => {
    const theme = createTheme(getMuiThemeConfig(mode), locale);
    theme.components = {
        ...theme.components,
        // Border-top on alerts, but it looks a bit messy
        MuiAlert: {
            // styleOverrides: {
            //     standard: {
            //         borderTopLeftRadius: 0,
            //         borderTopRightRadius: 0,
            //         borderTopStyle: 'solid',
            //         borderTopWidth: '3px',
            //     },
            //     standardSuccess: {
            //         borderColor: theme.palette.success.main,
            //     },
            //     standardError: {
            //         borderColor: theme.palette.error.main,
            //     },
            //     standardInfo: {
            //         borderColor: theme.palette.info.main,
            //     },
            //     standardWarning: {
            //         borderTopColor: theme.palette.warning.main,
            //     },
            // }
        },
    };
    return theme;
};

// ----------------------------------------------------------- //
// ----------------------------------------------------------- //
// Helpers
// ----------------------------------------------------------- //
// ----------------------------------------------------------- //

export type { Theme };

declare module '@mui/material' {
    interface Theme {
        bino: BinoTheme;
    }
}
declare module '@mui/material/styles' {
    // allow configuration using `createTheme`
    interface ThemeOptions {
        bino: BinoTheme;
    }

    interface Palette {
        white: Palette['primary'];
        gray: Palette['primary'];
    }

    interface PaletteOptions {
        white: PaletteOptions['primary'];
        gray: PaletteOptions['primary'];
    }

    interface TypographyVariants {
        label: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        label?: React.CSSProperties;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        white: true;
        gray: true;
    }

    interface ButtonPropsVariantOverrides {
        containedInverted: true;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        h6: false;
        label: true;
    }
}
export const getThemeColor =
    (theme: Theme) =>
    (key: NamedColors): string =>
        theme.bino.color[key];

export const useIsScreenSmallerThanBreakpoint = (size: Breakpoint = 'md') => {
    return useMediaQuery<Theme>((theme) => theme.breakpoints.down(size), {
        // https://github.com/mui-org/material-ui/issues/21048
        noSsr: true,
    });
};

export const useIsScreenLargerThanBreakpoint = (size: Breakpoint = 'md') => {
    return useMediaQuery<Theme>((theme) => theme.breakpoints.up(size), {
        // https://github.com/mui-org/material-ui/issues/21048
        noSsr: true,
    });
};
